import React from 'react';
import {
  Brief,
  Button,
  Gutter,
  HeaderLink,
  ImageCircle,
  Layout,
  Paragraph,
  ParagraphHeader,
  SectionHeader,
  useApi,
} from '@axiom/ui';
import { CandidateImageConst } from '@axiom/const';
import { Link } from 'react-router';
import type { LimitedCandidate } from '@axiom/validation';

import { CandidateUtil } from '../../utils/candidate-util';
import { LinkUtil } from '../../utils/link-util';
import { CandidateApi } from '../../api/candidate-api';
import { TaxonomyApi } from '../../api/taxonomy-api';

const { PossibleImageSizes } = CandidateImageConst;

export interface NearestNeighborProps {
  candidate?: LimitedCandidate;
  handleReachOutClick: () => void;
}

const getAddress = (talent: LimitedCandidate) => {
  const data = [];
  if (talent.addressState) data.push(talent.addressState);
  if (talent.addressCountry) data.push(talent.addressCountry);

  return data.join(', ');
};

export const NearestNeighbor = ({
  candidate,
  handleReachOutClick,
}: NearestNeighborProps) => {
  const [{ data: nearestNeighborData } = { data: [] }, { data: taxonomy }] =
    useApi(
      candidate?.id
        ? CandidateApi.readCandidatesSimilarTo(candidate.id)
        : undefined,
      TaxonomyApi.readTaxonomy()
    );
  return (
    <div data-test="nearest-neighbor-container">
      {nearestNeighborData.length > 0 ? (
        <>
          <SectionHeader>Similar Talent</SectionHeader>
          <Gutter bottom="16px" />
          <Brief
            briefMaxChildren={5}
            pattern="secondary"
            name="nearest-neighbor"
          >
            {nearestNeighborData.map(candidateNeighbor => (
              <Gutter
                bottom="16px"
                key={candidateNeighbor.id}
                name="nearest-neighbor-item"
              >
                <Layout position="middle" horizontalGutter="16px">
                  <ImageCircle
                    name="nearest-neighbor-image"
                    imageName={candidateNeighbor.calculatedDisplayName}
                    src={CandidateUtil.getProfileImageUri(
                      candidateNeighbor,
                      PossibleImageSizes.W_56
                    )}
                  />
                  <div>
                    <HeaderLink>
                      <Link
                        data-test="nearest-neighbor-link"
                        to={LinkUtil.getShareUrl(candidateNeighbor, taxonomy)}
                      >
                        <ParagraphHeader name="nearest-neighbor-candidate-name">
                          {candidateNeighbor.calculatedDisplayName}
                        </ParagraphHeader>
                      </Link>
                    </HeaderLink>
                    <Gutter bottom="4px" />
                    <div data-test="nearest-neighbor-address">
                      {getAddress(candidateNeighbor)}
                    </div>
                  </div>
                </Layout>
              </Gutter>
            ))}
            {nearestNeighborData.length > 5 ? (
              <div data-test="nearest-neighbor-see-more">
                <SectionHeader name="nearest-neighbor-see-more-title">
                  Want to see more?
                </SectionHeader>
                <Gutter bottom="12px" />
                <Paragraph>
                  If you'd like help finding more talent like this, including
                  available talent whose profiles aren't visible here yet,
                  please reach out to Axiom.
                </Paragraph>
                <Gutter bottom="16px" />
                <Button
                  name="nearest-neighbor-reach-out-button"
                  onClick={() => {
                    handleReachOutClick();
                  }}
                  pattern="secondary"
                  variation="outline"
                >
                  Reach Out
                </Button>
                <Gutter bottom="16px" />
              </div>
            ) : null}
          </Brief>
        </>
      ) : null}
    </div>
  );
};
